import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FileText, RotateCw, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { studioHeaders } from "@/lib/studioAuth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUS = { new: "New", quoted: "Quoted", won: "Won", lost: "Lost" };
const STATUS_CLS = { new: "bg-cafe-terracotta/15 text-cafe-terracotta", quoted: "bg-[#D4A574]/20 text-[#8A6A3E]", won: "bg-cafe-olive text-cafe-cream", lost: "bg-cafe-mocha/15 text-cafe-mocha" };
const EXTRA_LABEL = { models: "Models", styling: "Styling/props", makeup: "Makeup/hair", multi_location: "Multiple locations", studio: "Studio", voice_over: "Voice-over", animation: "Animation", extra_versions: "Extra versions", urgent: "Urgent", ads_usage: "Ads usage" };

function Field({ k, v }) {
    if (v === null || v === undefined || v === "" || (Array.isArray(v) && !v.length)) return null;
    return <div><span className="text-cafe-mocha">{k}: </span><span className="text-cafe-espresso">{Array.isArray(v) ? v.map((e) => EXTRA_LABEL[e] || e).join(", ") : v}</span></div>;
}

export default function QuotationsPanel() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/admin/quotation-requests`, { headers: studioHeaders() });
            setRows(data || []);
        } catch { toast.error("Failed to load quotation requests"); } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const setStatus = async (id, status) => {
        try {
            const { data } = await axios.post(`${API}/admin/quotation-requests/${id}/status`, { status }, { headers: studioHeaders() });
            setRows((p) => p.map((r) => (r.id === id ? data : r)));
            toast.success(`Marked ${STATUS[status]}`);
        } catch { toast.error("Update failed"); }
    };
    const remove = async (id) => {
        if (!window.confirm("Delete this quotation request?")) return;
        try { await axios.delete(`${API}/admin/quotation-requests/${id}`, { headers: studioHeaders() }); setRows((p) => p.filter((r) => r.id !== id)); } catch { toast.error("Delete failed"); }
    };
    const fresh = rows.filter((r) => r.status === "new").length;

    return (
        <div className="bg-cafe-latte/60 border border-border rounded-3xl p-6 md:p-8 mb-8" data-testid="quotations-panel">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <button type="button" onClick={() => setOpen((o) => !o)} className="flex items-center gap-3 text-left" data-testid="quotations-toggle">
                    <FileText className="h-5 w-5 text-cafe-olive" />
                    <div>
                        <h3 className="font-display text-xl text-cafe-espresso">Quotation requests {fresh > 0 && <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-cafe-terracotta text-cafe-cream align-middle" data-testid="quotations-new-count">{fresh} new</span>}</h3>
                        <p className="text-xs text-cafe-mocha mt-0.5">Briefs from the "Request a quotation" form on the pricing page. Reply with exact inclusions, then mark Quoted / Won.</p>
                    </div>
                </button>
                <Button variant="outline" onClick={load} className="rounded-full border-cafe-espresso/20 bg-transparent hover:bg-cafe-cream h-9 px-4 text-xs"><RotateCw className="h-3.5 w-3.5 mr-1" /> Refresh</Button>
            </div>
            {open && (loading ? <p className="text-sm text-cafe-mocha">Loading…</p> : rows.length === 0 ? <p className="text-sm text-cafe-mocha" data-testid="quotations-empty">No quotation requests yet.</p> : (
                <div className="space-y-3">
                    {rows.map((r) => (
                        <div key={r.id} className="bg-cafe-cream/70 border border-border rounded-2xl p-4" data-testid={`quotation-row-${r.id}`}>
                            <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
                                <div>
                                    <div className="font-semibold text-cafe-espresso text-sm">{r.package_name_en} <span className="text-cafe-mocha font-normal">· RM {r.package_price}</span></div>
                                    <div className="text-xs text-cafe-mocha">{r.customer_name}{r.business_name ? ` · ${r.business_name}` : ""} · <a href={`mailto:${r.customer_email}`} className="underline">{r.customer_email}</a>{r.customer_phone ? ` · ${r.customer_phone}` : ""}</div>
                                    <div className="text-[11px] text-cafe-mocha/70 mt-0.5">{new Date(r.created_at).toLocaleString("en-MY", { dateStyle: "medium", timeStyle: "short" })} · {r.source || "form"} · {(r.lang || "en").toUpperCase()}</div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className={`text-[10px] uppercase tracking-[0.18em] font-semibold px-2 py-1 rounded-full ${STATUS_CLS[r.status] || ""}`}>{STATUS[r.status] || r.status}</span>
                                    <select value={r.status} onChange={(e) => setStatus(r.id, e.target.value)} data-testid={`quotation-status-${r.id}`} className="h-8 bg-white border border-border rounded-lg px-2 text-xs">
                                        {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                                    </select>
                                    <Button variant="outline" size="icon" onClick={() => remove(r.id)} data-testid={`quotation-delete-${r.id}`} className="h-8 w-8 rounded-full border-cafe-espresso/20 bg-transparent hover:bg-red-50 hover:text-red-700"><Trash2 className="h-3.5 w-3.5" /></Button>
                                </div>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1 text-xs">
                                <Field k="Location" v={r.filming_location_label_en} /><Field k="Detail" v={r.location_detail} />
                                <Field k="Menu items" v={r.menu_items} /><Field k="Subjects" v={r.subjects_count} /><Field k="Locations" v={r.locations_count} />
                                <Field k="Final images" v={r.final_images} /><Field k="Final videos" v={r.final_videos} /><Field k="Preferred date" v={r.preferred_date} />
                                <Field k="Extras" v={r.extras} />
                            </div>
                            {r.notes && <p className="text-xs text-cafe-espresso mt-2 whitespace-pre-wrap border-t border-border pt-2">{r.notes}</p>}
                        </div>
                    ))}
                </div>
            ))}
        </div>
    );
}
